import { forwardRef } from "react";

import { ChevronDownIcon } from "@eisox/icons";
import * as RadixAccordion from "@radix-ui/react-accordion";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Accordion
 * -----------------------------------------------------------------------------------------------*/

type AccordionProps = RadixAccordion.AccordionSingleProps | RadixAccordion.AccordionMultipleProps;

const Root = RadixAccordion.Root;

Root.displayName = "AccordionRoot";

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

type ItemProps = RadixAccordion.AccordionItemProps;

const Item = forwardRef<HTMLDivElement, ItemProps>(({ className, ...props }, ref) => (
  <RadixAccordion.Item
    {...props}
    ref={ref}
    className={cn("border-b border-primary-darker-gray last:border-none", className)}
  />
));

Item.displayName = "AccordionItem";

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = RadixAccordion.AccordionTriggerProps;

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(({ className, children, ...props }, ref) => (
  <RadixAccordion.Header className={"flex"}>
    <RadixAccordion.Trigger
      {...props}
      ref={ref}
      className={cn(
        `gap-4 px-0 py-4 font-medium ease-out flex w-full cursor-pointer items-center justify-between rounded-[10px] text-left text-base transition-all duration-200 outline-none hover:underline focus-visible:border-2 focus-visible:border-primary-blue disabled:pointer-events-none disabled:opacity-50 [&[data-state="open"]>svg]:rotate-180`,
        className,
      )}
    >
      {children}
      <ChevronDownIcon className="h-4 w-4 ease-in-out pointer-events-none text-primary-dark-gray transition-transform duration-200" />
    </RadixAccordion.Trigger>
  </RadixAccordion.Header>
));

Trigger.displayName = "AccordionTrigger";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = RadixAccordion.AccordionContentProps;

const Content = forwardRef<HTMLDivElement, ContentProps>(({ className, children, ...props }, ref) => (
  <RadixAccordion.Content {...props} ref={ref} className={cn("accordion-content overflow-hidden text-base")}>
    <div className={cn("pt-0 pb-2.5", className)}>{children}</div>
  </RadixAccordion.Content>
));

Content.displayName = "AccordionContent";

export { Content, Item, Root, Trigger };
export type { AccordionProps, ContentProps, ItemProps, TriggerProps };
